import { activeLevels, distributionJobStatus, formatAmount } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetCommunityFinancialInfo } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { RECEIVER, SOURCE_AMT, RUN_STATUS, CREATED_ON, RULE, RUN_ON, TABLE_DATA, TRIGGER_SOURCE, SOURCE } from "./consts";
import moment from "moment";

const useViewJobGlDetails = () => {
  const { id, budgetId, glId } = useParams();
  const location = useLocation();
  const isMasterDataRoute = location.pathname.toLowerCase().includes(routes.masterData);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  const distributionJobDtl = useSelector((state: RootState) => state.currentData.distributionJobDtl);
  const communityName = communityData?.name || communityData?.communityName || "";
  const [activeLevel, setActiveLevel] = useState<"ZONE" | "BLOCK" | "PROPERTY">("ZONE");
  const [openGroupGl, setOpenGroupGl] = useState<boolean>(false);

  const form = useForm<any>({
    defaultValues: {
      [SOURCE]: "",
      [RECEIVER]: "",
      [SOURCE_AMT]: "",
      [RUN_STATUS]: "",
      [RULE]: "",
      [TRIGGER_SOURCE]: "",
      [CREATED_ON]: "",
      [RUN_ON]: "",
      [TABLE_DATA]: [],
    }  
  });

  const tableData = useWatch({control: form.control, name: TABLE_DATA});

  const communityId = (id || communityData?.communityId) as string;
  const budgetListPath = isMasterDataRoute
    ? `/${routes.admin}/${routes.masterData}/${routes.budgetMgmt}`
    : `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${communityId}/${routes.budgetMgmt}`;

  const breadcrumbData = useMemo(() => {
    const currentPage = {
      link: "",
      name: <span className="text-primary">GL - {distributionJobDtl?.glAccountNumber}</span>,
    };
    const crumbs = [
      {
        link: budgetListPath,
        name: "Budget Management",
      },
      {
        link: `${budgetListPath}/${budgetId}/simulate`,
        name: "Simulate Job",
      },
      currentPage,
    ];

    if (isMasterDataRoute) {
      return crumbs;
    }

    return [
      {
        link: `/${routes.admin}/${routes.adminConsole}/${routes.communities}`,
        name: `Community - ${communityName || ""}`,
      },
      ...crumbs,
    ];
  }, [budgetId, budgetListPath, communityName, distributionJobDtl, isMasterDataRoute]);

  const title = `GL - ${distributionJobDtl?.glAccountNumber} Details - ${communityName}(${currentFinanceYear})`;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const {data: financialInfo, isLoading: isLoadingFinancialInfo } = useGetCommunityFinancialInfo({
    community_id: communityData?.communityId,
    queryParams: {
      enabled: !!(communityData?.communityId)
    }
  })

  const activeThresholdIndex = useMemo(() => activeLevels.findIndex(
    (level) => level.value == financialInfo?.data?.distributionLevel
  ), [financialInfo])

  const activeLevelIndex = useMemo(() => activeLevels.findIndex(
    (level) => level.value == activeLevel
  ), [activeLevel])

  useEffect(() => {
    setActiveLevel(distributionJobDtl?.receiverLevel?.toUpperCase() || "ZONE");
  }, [distributionJobDtl]);


  const jobGlDetails = distributionJobDtl?.simulateDtl?.glDetails?.[glId as any];

  const isZoneLevel = activeLevel == "ZONE";
  const isBlockLevel = activeLevel == "BLOCK";
  const isPropertyLevel = activeLevel == "PROPERTY";

  useEffect(() => {
    if (jobGlDetails) {
      const data = jobGlDetails;
      let tableData: any = [];

      if (isZoneLevel) {
        tableData = data?.communityToZone?.map((val: any) => ({...val}) )
      } else if (isBlockLevel) {
        tableData = data?.communityToZone?.map((val: any) => ({...val, sharePct: '100',
          children: data?.zoneToBlock
          ?.filter((block: any) => block?.parentId == val?.receiverId)
          ?.map((block: any) => ({...block, sharePct: ((block?.distributedAmount * 100) / val?.distributedAmount), children: []}))
        }) )
      } else if (isPropertyLevel) {
        tableData = data?.communityToZone?.map((val: any) => ({...val, sharePct: '100',
          children: data?.zoneToBlock?.filter((block: any) => block?.parentId == val?.receiverId)?.map((block: any) => ({...block, sharePct: ((block?.distributedAmount * 100) / val?.distributedAmount),
            children: data?.blockToProperty?.filter((prop: any) => prop?.parentId == block?.receiverId)?.map((prop: any) => ({...prop, sharePct: ((prop?.distributedAmount * 100) / block?.distributedAmount)}))
          }))
        }))
      }

      form.reset({
        [SOURCE]: activeLevels[activeLevelIndex]?.source || "",
        [RECEIVER]: activeLevels[activeLevelIndex]?.receiver || "",
        [SOURCE_AMT]: data?.[SOURCE_AMT] || "",
        [RUN_STATUS]: distributionJobStatus[data?.[RUN_STATUS]?.toLowerCase()]?.label || "",
        [RULE]: isZoneLevel ? data?.zoneRule : isBlockLevel ? data?.blockRule : isPropertyLevel ? data?.propertyRule : "",
        [TRIGGER_SOURCE]: data?.[TRIGGER_SOURCE] || "",
        [CREATED_ON]: data?.[CREATED_ON] ? moment(data?.[CREATED_ON]).format('MM/DD/YYYY') : "",
        [RUN_ON]: data?.[RUN_ON] ?  moment(data?.[RUN_ON]).format('MM/DD/YYYY') : "",
        [TABLE_DATA]: tableData,
      })
    }
  }, [jobGlDetails, activeLevel])

  const total = useMemo(() => {
    if (tableData) {
      let metricValue = 0, share = 0, amt = 0;

      tableData?.map((data: any) => {
        metricValue += Number(data?.metricValue || "0");
        share += Number(data?.sharePct || "0");
        amt += Number(data?.distributedAmount || "0");
      });

      return {metricValue, share: share.toFixed(2), amt: formatAmount(amt, 2)}
    }
    return {metricValue: 0, share: 0, amt: 0}
  }, [tableData]);

  const parentColumns = [
    {
      accessorKey: "receiverName",
      header: <div></div>,
      accessorFn: (row: any) => String(row?.receiverName || ""),
    },
    {
      accessorKey: "metricValue",
      header: <div>Metric Value</div>,
      accessorFn: (row: any) => String(row?.metricValue || "0"),
    },
    {
      accessorKey: "sharePct",
      header: <div>Share %</div>,
      accessorFn: (row: any) => Number(row?.sharePct || "").toFixed(2),
    },
    {
      accessorKey: "distributedAmount",
      meta: { align: "right" },
      header: <div>Distributed Amount</div>,
      accessorFn: (row: any) => formatAmount(row?.distributedAmount || "0", 2),
    },
  ];

  const columns = [
    {
      accessorKey: "receiverName",
      header: <div>Receiver</div>,
      accessorFn: (row: any) => String(row?.receiverName || ""),
    },
    {
      accessorKey: "metricValue",
      header: <div>Metric Value</div>,
      accessorFn: (row: any) => String(row?.metricValue || "0"),
    },
    {
      accessorKey: "sharePct",
      header: <div>Share %</div>,
      accessorFn: (row: any) => Number(row?.sharePct || "").toFixed(2),
    },
    {
      accessorKey: "distributedAmount",
      meta: { align: "right" },
      header: <div>Distributed Amount</div>,
      accessorFn: (row: any) => formatAmount(row?.distributedAmount || "0", 2),
    },
  ];

  const onLevelChange = (level: string) => () => {
    setActiveLevel(level as any);
  }
  
  const onOpenGlDialog = () => {
    setOpenGroupGl(true)
  }

  return {
    title,
    breadcrumbData,
    isLoadingFinancialInfo,
    activeThresholdIndex,
    form,
    columns,
    total,
    distributionJobDtl,
    onLevelChange,
    activeLevelIndex,
    isZoneLevel,
    isBlockLevel,
    isPropertyLevel,
    parentColumns,
    onOpenGlDialog,
    openGroupGl,
    setOpenGroupGl,
    groupGlMasterId: distributionJobDtl?.glMasterId,
  }
}

export default useViewJobGlDetails;
